import { Component, effect, inject, OnInit, signal, viewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { TranslatePipe } from '@ngx-translate/core';
import { CalendarOptions, EventInput } from '@fullcalendar/core';
import kaLocale from '@fullcalendar/core/locales/ka';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { BookingService, EmployeeService, OnlineBookingService } from '@beautyapp/core';
import { format, addMinutes } from 'date-fns';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { FormsModule } from '@angular/forms';
import { map } from 'rxjs/operators';
import { NzSpaceComponent, NzSpaceItemDirective } from 'ng-zorro-antd/space';
import { DatepickerComponent } from '../../../../../../form/src/lib/datepicker/datepicker.component';
import { SelectComponent } from '../../../../../../form/src/lib/select/select.component';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { Store } from '@ngxs/store';
import { AuthState } from '@beautyapp/auth';

@Component({
  selector: 'beautyapp-service-reschedule-drawer',
  standalone: true,
  templateUrl: './service-reschedule-drawer.component.html',
  styleUrls: ['./service-reschedule-drawer.component.scss'],
  imports: [
    DatePipe,
    FullCalendarModule,
    TranslatePipe,
    DatepickerComponent,
    SelectComponent,
    NzButtonModule,
    FormsModule,
    NzIconModule,
    NzSpaceComponent,
    NzSpaceItemDirective,
    NzAlertModule,
    NzTagModule
  ]
})
export class ServiceRescheduleDrawerComponent implements OnInit {
  store = inject(Store);
  data = inject(NZ_DRAWER_DATA);
  drawerRef = inject(NzDrawerRef);
  employeeService = inject(EmployeeService);
  bookingService = inject(BookingService);
  onlineBookingService = inject(OnlineBookingService);

  currentDate: Date = new Date();
  selectedEmployeeId: string | null = null;
  employees = signal<any[]>([]);
  selectedSlot = signal<{ start: Date; end: Date } | null>(null);
  busyTimes = signal<EventInput[]>([]);
  scheduleEvents = signal<EventInput[]>([]);
  currentSlotEvent = signal<EventInput[]>([]);
  loading = signal<boolean>(false);

  calendarComponent = viewChild<FullCalendarComponent>('calendar');
  timeInterval = '00:15:00';

  calendarOptions: CalendarOptions = {
    locales: [kaLocale],
    locale: 'ka',
    timeZone: 'local',
    plugins: [
      resourceTimeGridPlugin,
      interactionPlugin,
      timeGridPlugin
    ],
    initialView: 'timeGridDay',
    editable: true,
    eventDurationEditable: false,
    weekends: true,
    selectable: true,
    nowIndicator: true,
    height: '100%',
    select: this.handleTimeSelect.bind(this),
    eventDrop: this.handleEventDrop.bind(this),
    slotLabelInterval: '01:00',
    slotDuration: this.timeInterval,
    headerToolbar: false,
    eventTimeFormat: {
      hour: '2-digit',
      minute: '2-digit',
      omitZeroMinute: false,
      hour12: false
    },
    slotLabelFormat: [
      {
        hour: '2-digit',
        minute: '2-digit',
        omitZeroMinute: false,
        meridiem: false,
        hour12: false
      }
    ],
    allDaySlot: false,
    slotMinTime: '06:00:00',
    slotMaxTime: '24:00:00'
  };

  constructor() {
    effect(() => {
      const events = [...this.busyTimes(), ...this.scheduleEvents(), ...this.currentSlotEvent()];
      this.calendarComponent()?.getApi()?.removeAllEventSources();
      if (events.length) {
        this.calendarComponent()?.getApi()?.addEventSource(events);
      }
    });
  }

  ngOnInit() {
    // Set working hours from branch settings
    this.store.select(AuthState.getActiveBranch).subscribe((branch: any) => {
      if (branch) {
        const startTime = `0${branch.whStart}`.slice(-2);
        const endTime = `0${branch.whEnd}`.slice(-2);
        this.calendarOptions.slotMinTime = `${startTime}:00:00`;
        this.calendarOptions.slotMaxTime = `${endTime}:00:00`;
      }
    });

    // Set initial employee from service data
    this.selectedEmployeeId = this.data.employeeId || null;

    // Set initial date from service data
    if (this.data.currentDate) {
      const date = new Date(this.data.currentDate);
      this.currentDate = date;
      setTimeout(() => {
        this.calendarComponent()?.getApi()?.gotoDate(date);
      });
    }

    this.loadEmployees();
    this.loadBusyTimes();
    this.loadEmployeeSchedule();
    this.loadCurrentSlot();
  }

  loadCurrentSlot() {
    // Show current slot only if we're viewing the original booking date
    const currentDateStr = format(this.currentDate, 'yyyy-MM-dd');
    const originalDateStr = this.data.currentDate;

    if (currentDateStr === originalDateStr && this.data.currentStartTime && this.data.currentEndTime) {
      const startTime = this.data.currentStartTime.includes(' ')
        ? this.data.currentStartTime.split(' ')[1]
        : this.data.currentStartTime;
      const endTime = this.data.currentEndTime.includes(' ')
        ? this.data.currentEndTime.split(' ')[1]
        : this.data.currentEndTime;

      this.currentSlotEvent.set([{
        id: 'current-slot',
        start: `${currentDateStr}T${startTime}:00`,
        end: `${currentDateStr}T${endTime}:00`,
        backgroundColor: '#1890ff',
        borderColor: '#096dd9',
        title: 'მიმდინარე დრო',
        editable: true
      }]);
    } else {
      this.currentSlotEvent.set([]);
    }
  }

  loadEmployees() {
    this.employeeService.getDropdown().pipe(
      map((res: any[]) => res.map((emp: any) => ({
        ...emp,
        fullName: emp.user?.fullName || `${emp.user?.firstname || ''} ${emp.user?.lastname || ''}`.trim()
      })))
    ).subscribe((employees) => {
      this.employees.set(employees);
    });
  }

  changeEmployee(employeeId: string) {
    this.selectedEmployeeId = employeeId;
    this.selectedSlot.set(null);
    this.calendarComponent()?.getApi()?.getEventById('selected-slot')?.remove();
    this.loadBusyTimes();
    this.loadEmployeeSchedule();
  }

  loadEmployeeSchedule() {
    if (!this.selectedEmployeeId) return;

    this.employeeService.getEmployeesSchedule({
      employeeIds: [this.selectedEmployeeId],
      date: format(this.currentDate, 'yyyy-MM-dd')
    }).pipe(
      map((employees: any[]) => {
        const events: EventInput[] = [];
        const emp = employees.find((e: any) => e.id === this.selectedEmployeeId);

        // Check if employee has no schedule (day off)
        if (!emp || !emp.schedule || !emp.schedule.shiftStart || !emp.schedule.shiftEnd) {
          // Mark entire day as non-working
          events.push({
            id: 'day-off',
            start: `${format(this.currentDate, 'yyyy-MM-dd')}T${this.calendarOptions.slotMinTime}`,
            end: `${format(this.currentDate, 'yyyy-MM-dd')}T${this.calendarOptions.slotMaxTime}`,
            display: 'background',
            backgroundColor: '#8c8c8c',
            title: 'დასვენების დღე'
          });
          return events;
        }

        // Mark non-working hours before shift
        events.push({
          id: `before-shift-${emp.id}`,
          start: `${format(this.currentDate, 'yyyy-MM-dd')}T${this.calendarOptions.slotMinTime}`,
          end: `${format(this.currentDate, 'yyyy-MM-dd')}T${emp.schedule.shiftStart}:00`,
          display: 'background',
          backgroundColor: '#8c8c8c',
          title: 'არასამუშაო დრო'
        });

        // Mark non-working hours after shift
        events.push({
          id: `after-shift-${emp.id}`,
          start: `${format(this.currentDate, 'yyyy-MM-dd')}T${emp.schedule.shiftEnd}:00`,
          end: `${format(this.currentDate, 'yyyy-MM-dd')}T${this.calendarOptions.slotMaxTime}`,
          display: 'background',
          backgroundColor: '#8c8c8c',
          title: 'არასამუშაო დრო'
        });

        // Add breaks
        if (emp.schedule.breaks) {
          for (const brk of emp.schedule.breaks) {
            events.push({
              id: `break-${emp.id}-${brk.start}`,
              start: `${format(this.currentDate, 'yyyy-MM-dd')}T${brk.start}`,
              end: `${format(this.currentDate, 'yyyy-MM-dd')}T${brk.end}`,
              display: 'background',
              backgroundColor: '#dc939a',
              title: brk.type || 'შესვენება'
            });
          }
        }

        return events;
      })
    ).subscribe((events) => {
      this.scheduleEvents.set(events);
    });
  }

  loadBusyTimes() {
    if (!this.selectedEmployeeId) return;

    this.bookingService.employeeBusyTimes({
      date: format(this.currentDate, 'yyyy-MM-dd'),
      employeeId: this.selectedEmployeeId
    }).pipe(
      map((times: any[]) => {
        return times.map((t: any) => ({
          id: `busy-${t.startTime}-${t.endTime}`,
          start: `${format(this.currentDate, 'yyyy-MM-dd')}T${t.startTime}:00`,
          end: `${format(this.currentDate, 'yyyy-MM-dd')}T${t.endTime}:00`,
          display: 'background',
          backgroundColor: '#ff4d4f',
          title: 'დაკავებული'
        }));
      })
    ).subscribe((events) => {
      this.busyTimes.set(events);
    });
  }

  handleTimeSelect(info: any) {
    // Calculate end time based on service duration
    const duration = this.data.serviceDuration || 60;
    const endTime = addMinutes(info.start, duration);

    this.selectedSlot.set({
      start: info.start,
      end: endTime
    });

    // Add visual indicator for selected slot
    this.calendarComponent()?.getApi()?.getEventById('selected-slot')?.remove();
    this.calendarComponent()?.getApi()?.addEvent({
      id: 'selected-slot',
      start: info.start,
      end: endTime,
      backgroundColor: '#52c41a',
      borderColor: '#389e0d',
      title: 'არჩეული დრო',
      editable: true
    });
  }

  handleEventDrop(info: any) {
    // Only handle dragging of selected-slot or current-slot events
    if (info.event.id === 'selected-slot' || info.event.id === 'current-slot') {
      const duration = this.data.serviceDuration || 60;
      const endTime = addMinutes(info.event.start, duration);

      this.selectedSlot.set({
        start: info.event.start,
        end: endTime
      });

      // Update the event end time to maintain duration
      info.event.setEnd(endTime);

      // If it was the current slot being dragged, convert it to a selected slot
      if (info.event.id === 'current-slot') {
        info.event.remove();
        this.calendarComponent()?.getApi()?.getEventById('selected-slot')?.remove();
        this.calendarComponent()?.getApi()?.addEvent({
          id: 'selected-slot',
          start: info.event.start,
          end: endTime,
          backgroundColor: '#52c41a',
          borderColor: '#389e0d',
          title: 'არჩეული დრო',
          editable: true
        });
      }
    }
  }

  changeDate(date: Date) {
    this.currentDate = date;
    this.selectedSlot.set(null);
    this.calendarComponent()?.getApi()?.gotoDate(date);
    this.calendarComponent()?.getApi()?.getEventById('selected-slot')?.remove();
    this.loadBusyTimes();
    this.loadEmployeeSchedule();
    this.loadCurrentSlot();
  }

  prevDate() {
    const calendarApi = this.calendarComponent()?.getApi();
    if (calendarApi) {
      calendarApi.prev();
      this.currentDate = new Date(calendarApi.getDate());
      this.selectedSlot.set(null);
      this.calendarComponent()?.getApi()?.getEventById('selected-slot')?.remove();
      this.loadBusyTimes();
      this.loadEmployeeSchedule();
      this.loadCurrentSlot();
    }
  }

  nextDate() {
    const calendarApi = this.calendarComponent()?.getApi();
    if (calendarApi) {
      calendarApi.next();
      this.currentDate = new Date(calendarApi.getDate());
      this.selectedSlot.set(null);
      this.calendarComponent()?.getApi()?.getEventById('selected-slot')?.remove();
      this.loadBusyTimes();
      this.loadEmployeeSchedule();
      this.loadCurrentSlot();
    }
  }

  todayDate() {
    const today = new Date();
    this.currentDate = today;
    this.selectedSlot.set(null);
    this.calendarComponent()?.getApi()?.gotoDate(today);
    this.calendarComponent()?.getApi()?.getEventById('selected-slot')?.remove();
    this.loadBusyTimes();
    this.loadEmployeeSchedule();
    this.loadCurrentSlot();
  }

  changeTimeInterval(interval: string) {
    this.timeInterval = interval;
    this.calendarOptions.slotDuration = interval;
    this.calendarComponent()?.getApi()?.setOption('slotDuration', interval);
  }

  saveReschedule() {
    if (!this.selectedSlot()) return;

    this.loading.set(true);

    // Extract time directly to avoid timezone conversion issues
    const startDate = this.selectedSlot()!.start;
    const endDate = this.selectedSlot()!.end;
    const startTime = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`;
    const endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;

    this.onlineBookingService.rescheduleService({
      onlineBookingId: this.data.onlineBookingId,
      serviceIndex: this.data.serviceIndex,
      newDate: format(this.currentDate, 'yyyy-MM-dd'),
      newStartTime: startTime,
      newEndTime: endTime,
      newEmployeeId: this.selectedEmployeeId
    }).subscribe({
      next: () => {
        this.loading.set(false);
        this.drawerRef.close(true);
      },
      error: () => {
        this.loading.set(false);
      }
    });
  }

  cancel() {
    this.drawerRef.close(false);
  }
}
